import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { updateExpressionAction } from "actions/expressions";
import Button from "components/Common/Button";
import Checkbox from "components/Common/Checkbox";
import { ExpressionRecord } from "reducers/expressions/reducer";
import { NO_OP_FUNCTION } from "services/helpers";
import "./style.scss";

export const BLOCK_NAME = "ResponseEditorTrainerExpression";

export class ResponseEditorTrainerExpression extends React.Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.handleSelect = this.handleSelect.bind(this);
    this.handleSaveButtonClick = this.handleSaveButtonClick.bind(this);
    this.handleCancelButtonClick = this.handleCancelButtonClick.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);

    this.editRef = React.createRef();

    this.state = {
      inEditMode: false,
      canPressSave: false
    };
  }

  /**
   * @param {Object} prevProps
   * @param {Object} prevState
   */
  componentDidUpdate(prevProps, prevState) {
    const { inEditMode } = this.state;

    if (inEditMode && !prevState.inEditMode && this.editRef.current) {
      this.editRef.current.focus();
    }
  }

  /**
   * handler for cancel button (exits edit mode)
   */
  handleCancelButtonClick() {
    this.setState({
      inEditMode: false
    });
  }

  /**
   * handler for expression save button
   */
  handleSaveButtonClick() {
    const {
      expression,
      updateExpression
    } = this.props;

    if (!this.editRef.current) {
      return;
    }

    updateExpression(expression.id, this.editRef.current.value);

    this.setState({
      inEditMode: false
    });
  }

  /**
   * toggle checked state (handled by parent component)
   */
  handleSelect() {
    const { handleSelect, expression, checked } = this.props;

    handleSelect(expression.id, !checked);
  }

  /**
   * handler for hotkeys
   * @param {Event} event
   */
  handleKeyPress(event) {
    const { canPressSave } = this.state;

    if (event.keyCode === 13 && canPressSave) {
      // enter
      this.handleSaveButtonClick();
    } else if (event.keyCode === 27) {
      // escape
      this.handleCancelButtonClick();
    }
  }

  /**
   * resets state when leaving edit mode
   */
  resetEditMode() {
    this.setState({
      inEditMode: false,
      canPressSave: false
    });
  }

  /**
   * @returns {ReactElement|null}
   */
  renderExpressionControls() {
    const {
      checked,
      readOnly
    } = this.props;

    if (readOnly) {
      return null;
    }

    return (
      <>
        <Button
          onClick={() => {
            this.setState({
              inEditMode: true
            });
          }}
          customClassName={`${BLOCK_NAME}__edit-button`}
          className={`${BLOCK_NAME}__edit-button`}
          title="Edit Training"
          text="Edit"
          size="small"
          light
        />
        <Checkbox
          checked={checked}
          className={classNames(
            `${BLOCK_NAME}__checkbox g-tile__checkbox`,
            {
              [`${BLOCK_NAME}__checkbox--checked`]: checked
            }
          )}
          handleToggle={this.handleSelect}
          size="small"
        />
      </>
    );
  }

  /**
   * @returns {ReactElement}
   */
  renderEditMode() {
    const { customClassName, expression } = this.props;
    const { canPressSave } = this.state;

    return (
      <div
        className={`${BLOCK_NAME}`}
        key={expression.id}
      >
        <div
          className={classNames(`${BLOCK_NAME}__body ${BLOCK_NAME}__body--edit-mode`, {
            customClassName
          })}
        >
          <input
            type="text"
            className={`${BLOCK_NAME}__edit-input g-input`}
            placeholder="Type a question"
            defaultValue={expression.body}
            ref={this.editRef}
            onKeyDown={this.handleKeyPress}
            onChange={() => {
              this.setState({
                canPressSave:
                  this.editRef.current.value !== expression.body &&
                  this.editRef.current.value !== ""
              });
            }}
          />
          <Button
            onClick={this.handleSaveButtonClick}
            customClassName={`${BLOCK_NAME}__save-edit-button`}
            disabled={!canPressSave}
            title="Save"
            text="Save"
            size="small"
          />
          <Button
            onClick={() => {
              this.setState({
                inEditMode: false
              });
            }}
            customClassName={`${BLOCK_NAME}__cancel-edit-button`}
            className={`${BLOCK_NAME}__cancel-edit-button`}
            title="Cancel"
            text="Cancel"
            size="small"
            light
          />
        </div>
      </div>
    );
  }

  /**
   * @returns {ReactElement}
   */
  renderViewOnlyMode() {
    const {
      customClassName,
      expression
    } = this.props;

    return (
      <div
        className={`${BLOCK_NAME}`}
        key={expression.id}
      >
        <div
          className={classNames(`${BLOCK_NAME}__body`, {
            customClassName
          })}
          title={expression.body}
        >
          {expression.body}
        </div>
        {this.renderExpressionControls()}
      </div>
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const { inEditMode } = this.state;

    return inEditMode ? this.renderEditMode() : this.renderViewOnlyMode();
  }
}

ResponseEditorTrainerExpression.propTypes = {
  expression: PropTypes.instanceOf(ExpressionRecord).isRequired,
  customClassName: PropTypes.string,
  readOnly: PropTypes.bool,
  /* handleSelect: required unless readOnly == true */
  handleSelect: PropTypes.func,
  /* updateExpression: required unless readOnly == true */
  updateExpression: PropTypes.func,
  /* checked: required unless readOnly == true */
  checked: PropTypes.bool
};

ResponseEditorTrainerExpression.defaultProps = {
  customClassName: "",
  readOnly: false,
  handleSelect: NO_OP_FUNCTION,
  updateExpression: NO_OP_FUNCTION,
  checked: false
};

/**
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatch(dispatch) {
  return bindActionCreators({
    updateExpression: updateExpressionAction
  }, dispatch);
}

const Connector = connect(null, mapDispatch)(ResponseEditorTrainerExpression);

export default Connector;
